import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  useGetInvitePreviewQuery,
  useCompleteInviteSignupMutation,
  localApi,
} from '../../store/api/localApi';
import { loginSuccess } from '../../store/slices/authSlice';
import { brand } from '../../branding';
import { EyeIcon, EyeSlashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PASSWORD_RULES, isPasswordValid } from '../../utils/password';

const ROLE_BADGE: Record<string, string> = {
  SUPER_ADMIN: 'bg-purple-100 text-purple-800',
  ORG_ADMIN: 'bg-blue-100 text-blue-800',
  LOCATION_ADMIN: 'bg-green-100 text-green-800',
  STAFF: 'bg-amber-100 text-amber-800',
  PARENT: 'bg-pink-100 text-pink-800',
};

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ORG_ADMIN: 'Org Admin',
  LOCATION_ADMIN: 'Location Admin',
  STAFF: 'Staff',
  PARENT: 'Parent',
};

const InviteSignup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');

  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = useGetInvitePreviewQuery(token, {
    skip: !token,
  });

  const [completeSignup, { isLoading: submitting }] = useCompleteInviteSignupMutation();

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated && !token) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!firstName.trim() || !lastName.trim()) {
      setValidationError('First name and last name are required');
      return;
    }
    if (!isPasswordValid(password)) {
      setValidationError(
        'Password must include an uppercase letter, a lowercase letter, a number, and a symbol.'
      );
      return;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    try {
      const result = await completeSignup({
        token,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      }).unwrap();
      dispatch(localApi.util.resetApiState());
      dispatch(
        loginSuccess({
          user: result.user,
          token: result.accessToken || result.token,
          refreshToken: (result as any).refreshToken ?? null,
        })
      );
      navigate('/auth/contact-details');
    } catch (err: any) {
      const data = err?.data || {};
      setValidationError(
        data.message || data.details?.[0]?.message || data.error || 'Failed to complete signup'
      );
    }
  };

  const errorData = previewError as any;
  const isExpired =
    errorData?.data?.code === 'INVITE_EXPIRED' || errorData?.data?.error?.includes?.('expired');
  const errorMessage = errorData?.data?.error || 'Failed to load invite details';

  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
        data-testid="invite-signup-page"
      >
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invite Link</h2>
            <p className="text-sm text-gray-600">
              This invite link is missing a token. Please check the link from your email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      data-testid="invite-signup-page"
    >
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white shadow rounded-lg p-8">
          <h2 className="text-center text-2xl font-bold text-gray-900 mb-6">You're Invited!</h2>

          {/* Loading state */}
          {previewLoading && (
            <div data-testid="invite-preview-loading" className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
              <div className="h-10 bg-gray-200 rounded" />
              <div className="h-10 bg-gray-200 rounded" />
              <div className="h-10 bg-gray-200 rounded" />
            </div>
          )}

          {/* Error state */}
          {previewError && (
            <div data-testid="invite-preview-error">
              {isExpired ? (
                <div className="text-center space-y-3">
                  <div className="text-amber-600 text-sm font-medium">Invitation Expired</div>
                  <p className="text-sm text-gray-600">
                    This invitation has expired. Please ask your administrator to resend the invite.
                  </p>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {errorMessage}
                </div>
              )}
            </div>
          )}

          {/* Preview loaded - show form */}
          {preview &&
            !previewError &&
            (() => {
              const invite = preview.invite || preview;
              return (
                <div className="space-y-6">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600">
                      {invite.invitedByName ? (
                        <>
                          <span className="font-medium">{invite.invitedByName}</span> has invited
                          you to join{' '}
                        </>
                      ) : (
                        <>You've been invited to join </>
                      )}
                      <strong>{invite.organizationName || brand.name}</strong>
                    </p>
                    {invite.locationName && (
                      <p className="text-sm text-gray-500">
                        Location: <span className="font-medium">{invite.locationName}</span>
                      </p>
                    )}
                    <p className="text-sm text-gray-500">
                      Role:{' '}
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          ROLE_BADGE[invite.role] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {ROLE_LABELS[invite.role] || invite.role}
                      </span>
                    </p>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    data-testid="invite-signup-form"
                    className="space-y-4"
                  >
                    {validationError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                        {validationError}
                      </div>
                    )}

                    <div>
                      <label
                        htmlFor="invite-email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>
                      <input
                        id="invite-email"
                        type="email"
                        name="email"
                        autoComplete="off"
                        value={invite.email || ''}
                        disabled
                        className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="invite-first-name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First Name
                        </label>
                        <input
                          id="invite-first-name"
                          type="text"
                          name="given-name"
                          autoComplete="given-name"
                          required
                          data-testid="invite-first-name-input"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="invite-last-name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last Name
                        </label>
                        <input
                          id="invite-last-name"
                          type="text"
                          name="family-name"
                          autoComplete="family-name"
                          required
                          data-testid="invite-last-name-input"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="invite-password"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Create a password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          id="invite-password"
                          type={showPassword ? 'text' : 'password'}
                          name="new-password"
                          autoComplete="new-password"
                          required
                          data-testid="invite-password-input"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 pr-10 sm:text-sm"
                          placeholder="Create a password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <ul
                        data-testid="invite-password-requirements"
                        className="mt-2 space-y-1 text-xs"
                      >
                        {PASSWORD_RULES.map((rule) => {
                          const passed = rule.test(password);
                          return (
                            <li
                              key={rule.id}
                              data-testid={`invite-password-rule-${rule.id}`}
                              data-passed={passed ? 'true' : 'false'}
                              className={`flex items-center gap-1.5 ${
                                passed ? 'text-green-700' : 'text-gray-500'
                              }`}
                            >
                              {passed ? (
                                <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              ) : (
                                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              )}
                              {rule.label}
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <div>
                      <label
                        htmlFor="invite-confirm-password"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Confirm password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          id="invite-confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirm-new-password"
                          autoComplete="new-password"
                          required
                          data-testid="invite-confirm-password-input"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 pr-10 sm:text-sm"
                          placeholder="Confirm password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={
                        submitting ||
                        !isPasswordValid(password) ||
                        password !== confirmPassword ||
                        !firstName.trim() ||
                        !lastName.trim()
                      }
                      data-testid="invite-submit-btn"
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Setting up...' : 'Set Up Account'}
                    </button>
                  </form>
                </div>
              );
            })()}
        </div>
      </div>
    </div>
  );
};

export default InviteSignup;
